/**
 * Recipe times at a serving count other than the one they were written for
 * (#1000).
 *
 * The serving counter scales every gram and used to leave every minute as
 * written: Pad Kee Mao said 27 minutes for 2 and took an hour for 4. Two
 * kinds of minutes move with the food, one does not:
 *
 *   - PREP scales linearly. It is work on the ingredients — wash, trim, cut —
 *     and twice the vegetables is twice the knife work.
 *   - BATCH-BOUND cook minutes (`recipe_steps.batch_bound`: searing,
 *     stir-frying, frying) grow with the serving count, because a home pan
 *     takes one layer and the rest waits for the next batch. Upward only:
 *     below the base the food still fits the pan the recipe was written for,
 *     so the clock is the pan's.
 *   - Everything else — simmer, bake, boil, rest, soak — is the vessel's
 *     clock and stays as written.
 *
 * Legacy rows carry `batch_bound = false`, so prep scales for every recipe
 * from day one and cook scaling arrives as recipes are generated or edited.
 */
export interface RecipeTimesInput {
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  /** Sum of `time_minutes` over the variant's batch-bound steps. */
  batchBoundMinutes: number;
  /**
   * Sum of `time_minutes` over the variant's `passive` steps — marinating,
   * proofing, chilling, resting, soaking, curing (#1004).
   *
   * This is the third kind of minute, and the one the page used to lose
   * entirely. `prepTimeMinutes` and `cookTimeMinutes` cover active work and
   * hands-off COOKING, and nothing in the generation prompt ever asked the
   * model to fold passive waiting into either, so it sat only in the step rows
   * and was never rendered anywhere: `mos-grilled-chicken` announced 50 minutes
   * on a Bougie variant whose step 5 refrigerates the chicken for four hours,
   * and its Classic announced 25 with the same marinade. Valid data, clean
   * render, and a cook planning dinner wrong by four hours.
   *
   * Does NOT scale. A marinade is a marinade at any serving count — this is the
   * dish's own clock, like a simmer, not work on the ingredients.
   */
  waitMinutes: number;
  /** The count the stored times are written for. */
  baseServings: number;
  /** The count the reader has selected. */
  servings: number;
}

export interface ScaledRecipeTimes {
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  /** Hands-on plus hands-off cooking: `prep + cook`. Excludes waiting. */
  totalTimeMinutes: number;
  /** Passive waiting, unscaled. See `RecipeTimesInput.waitMinutes`. */
  waitMinutes: number;
  /**
   * Start to plate: `prep + cook + wait`. This is what a reader means by "how
   * long does this take" — it is the number to put in front of them whenever
   * waiting is a material part of it.
   */
  elapsedTimeMinutes: number;
  /** True when the reader's count differs from the base and a time moved. */
  scaled: boolean;
}

/**
 * Below this, passive minutes are part of the flow rather than a plan-your-day
 * fact — a 3-minute rest before carving, a 5-minute steep while the pan heats.
 * Surfacing those as "waiting" would add noise to almost every recipe and teach
 * the reader to skip the line that matters on the ones with a real marinade.
 */
export const WAIT_DISPLAY_FLOOR_MINUTES = 15;

export function scaleRecipeTimes(input: RecipeTimesInput): ScaledRecipeTimes {
  const { prepTimeMinutes, cookTimeMinutes, batchBoundMinutes, baseServings, servings } = input;
  const wait = Math.max(0, input.waitMinutes);
  const factor = baseServings > 0 && servings > 0 ? servings / baseServings : 1;
  if (factor === 1) {
    return {
      prepTimeMinutes,
      cookTimeMinutes,
      totalTimeMinutes: prepTimeMinutes + cookTimeMinutes,
      waitMinutes: wait,
      elapsedTimeMinutes: prepTimeMinutes + cookTimeMinutes + wait,
      scaled: false,
    };
  }
  const prep = Math.ceil(prepTimeMinutes * factor);
  const cook =
    factor > 1 ? cookTimeMinutes + Math.ceil(Math.max(0, batchBoundMinutes) * (factor - 1)) : cookTimeMinutes;
  return {
    prepTimeMinutes: prep,
    cookTimeMinutes: cook,
    totalTimeMinutes: prep + cook,
    waitMinutes: wait,
    elapsedTimeMinutes: prep + cook + wait,
    scaled: prep !== prepTimeMinutes || cook !== cookTimeMinutes,
  };
}
